// Buluşma teklifi için tarih+saat seçici — web sürümü.
// @react-native-community/datetimepicker web'i desteklemiyor (mobile/ aynı
// zamanda react-native-web ile web'e de export ediliyor — architecture.html/
// mimari.html tam olarak bu export'tan üretiliyor), bu yüzden burada iki
// düz metin alanı (YYYY-MM-DD, HH:MM) kullanılıyor. Metro, web build'de bu
// dosyayı MeetupDateTimeField.tsx yerine otomatik seçer.
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, TextInput, View } from '../components/rn';
import { spacing, fontSize, formControlMetrics, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { normalizeInputDigits } from '../i18n/inputDigits';
import { parseDateTimeInput } from '../i18n/dateTimeInput';

type Props = {
  value: Date;
  onChange: (d: Date) => void;
  onValidityChange?: (valid: boolean) => void;
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

export default function MeetupDateTimeField({ value, onChange, onValidityChange }: Props) {
  const colors = useTheme();
  const { lang, t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [dateText, setDateText] = useState(
    `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
  );
  const [timeText, setTimeText] = useState(`${pad(value.getHours())}:${pad(value.getMinutes())}`);

  const timestamp = value.getTime();
  useEffect(() => {
    const current = new Date(timestamp);
    setDateText(`${String(current.getFullYear()).padStart(4, '0')}-${pad(current.getMonth() + 1)}-${pad(current.getDate())}`);
    setTimeText(`${pad(current.getHours())}:${pad(current.getMinutes())}`);
    onValidityChange?.(Number.isFinite(timestamp));
  }, [timestamp, onValidityChange]);

  function commit(nextDateText: string, nextTimeText: string) {
    const next = parseDateTimeInput(nextDateText, nextTimeText);
    onValidityChange?.(next !== null);
    if (next) onChange(next);
  }

  return (
    <View style={styles.row}>
      <TextInput
        style={styles.field}
        value={dateText}
        onChangeText={(text) => {
          const next = normalizeInputDigits(text, lang);
          setDateText(next); commit(next, timeText);
        }}
        placeholder="YYYY-MM-DD"
        accessibilityLabel={t('calendar.dateInputLabel')}
        accessibilityHint={t('calendar.dateInputHint')}
        placeholderTextColor={colors.gray400}
      />
      <TextInput
        style={styles.field}
        value={timeText}
        onChangeText={(text) => {
          const next = normalizeInputDigits(text, lang);
          setTimeText(next); commit(dateText, next);
        }}
        placeholder="HH:MM"
        accessibilityLabel={t('calendar.timeInputLabel')}
        accessibilityHint={t('calendar.timeInputHint')}
        placeholderTextColor={colors.gray400}
      />
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.md,
  },
  field: {
    flexGrow: 1,
    flexBasis: 0,
    flexShrink: 1,
    ...formControlMetrics,
    borderColor: colors.gray200,
    paddingVertical: 0,
    paddingHorizontal: 0,
    fontSize: fontSize.md,
    color: colors.black,
    textAlign: 'center',
  },
});
